/**
 * Making a stored phone number or email something a host can actually use.
 *
 * There was not one `tel:` or `mailto:` anywhere in Studio. A host standing in
 * a hotel lobby at 19:40, working out who is not at the bus, had the number on
 * screen and had to retype it into their phone — which is when digits get
 * dropped, and the person you cannot reach is the one you are looking for.
 *
 * These are the only two schemes here on purpose. `sms:` is not among them:
 * the concierge owns the SMS rail and a host texting a guest from their own
 * handset produces a thread the trip has no record of, on a number the guest
 * did not consent to. Calling is a phone call; messaging goes through
 * Broadcasts, where consent and history live.
 */

/**
 * A `tel:` URI, or '' when there is nothing safely dialable.
 *
 * RFC 3966 wants digits and a leading `+`; the spaces, parens and dots a
 * spreadsheet added are decoration a dialer has to be protected from. The `+`
 * is NOT stripped — it is the difference between a number that works from
 * Lisbon and one that does not, which is the whole situation this exists for.
 *
 * Refuses anything that is not ONE number. Stripping every non-digit and
 * concatenating what is left turns "317-414-6628 x412" into a thirteen-digit
 * wrong number and "317-414-6628 / 317-414-6629" into a twenty-digit one — the
 * exact "offering a wrong number" outcome the length floor was meant to
 * prevent, which it only ever caught when the extension was the entire value.
 * A separator, a letter or a second `+` means the cell holds more than a
 * number, and the honest answer is to show it without linking it.
 *
 * Bounds are E.164's: at least 7 digits to be a subscriber number, at most 15
 * because that is the standard's maximum. Same rule as `looksLikePhone` in
 * lib/columnMapping, which the importer has used since it shipped.
 */
export function telHref(phone: string | null | undefined): string {
  const raw = String(phone ?? '').trim();
  if (!raw) return '';
  // One optional leading +, then digits and punctuation that is only ever
  // grouping. Anything else — a letter, a slash, a comma, a second + — means
  // this cell is not a single dialable number.
  if (!/^\+?[0-9\s().-]+$/.test(raw)) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) return '';
  return `tel:${raw.startsWith('+') ? '+' : ''}${digits}`;
}

/**
 * A `mailto:` URI, or '' when the value cannot be one.
 *
 * Shape borrowed from `EMAIL_SHAPE` in lib/columnMapping rather than invented
 * again: something, an `@`, something with a dot in it, no spaces. Weak on
 * purpose — anything stricter rejects addresses that are valid, and the cost of
 * being wrong here is a compose window with a bad recipient, not a message
 * sent to a stranger.
 *
 * Nothing is percent-encoded. `encodeURIComponent` escapes both characters
 * that matter in an addr-spec: `@` becomes %40 (a local part with no domain)
 * and `+` becomes %2B, which breaks plus-addressing — common on imported
 * rosters — in any client that does not decode. The shape test above already
 * guarantees no whitespace and no characters that could break out of the URI.
 */
export function mailtoHref(email: string | null | undefined): string {
  const raw = String(email ?? '').trim();
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(raw) ? `mailto:${raw}` : '';
}

/** How a number should read on screen: as the host typed it, always. */
export function displayPhone(phone: string | null | undefined): string {
  return String(phone ?? '').trim();
}
